/**
 * Signing in when the internet is down.
 *
 * Signing in normally asks the server. When there is no connection at all, the
 * till would be locked at the exact moment it is needed most - the morning a
 * line is cut and the shop still has to open.
 *
 * So after every successful ONLINE sign-in, this device keeps a "verifier" for
 * that person: a slow one-way hash of the password with a random salt (PBKDF2,
 * the same idea a server uses). It cannot be turned back into the password. When
 * the server cannot be reached, the typed password is hashed the same way and
 * compared. A match restores the session this person last had here.
 *
 * Rules that keep this honest:
 *   - Only used when the failure is NO CONNECTION. A refusal from the server
 *     ("wrong password", "account closed") is final and never falls back.
 *   - Only for someone who has signed in on THIS device online before. Nothing
 *     is shipped to devices in advance.
 *   - A verifier older than MAX_AGE_DAYS is ignored - a person who has not been
 *     seen here for weeks should check in with the server first.
 *   - Deactivated staff are forgotten the next time the catalogue refreshes.
 */
import type { User } from '@pos/shared';
import { db, getMeta, type PosDb } from './db.js';

export interface OfflineSession {
  token: string;
  user: User;
  device: unknown;
}

interface StoredVerifier {
  salt: string;
  hash: string;
  iterations: number;
  savedAt: string;
  session: OfflineSession;
}

const PREFIX = 'offlineLogin:';
const ITERATIONS = 150_000;
const MAX_AGE_DAYS = 30;

const keyFor = (username: string) => PREFIX + username.trim().toLowerCase();

/**
 * Keep what is needed to let this person back in without the server. `target`
 * is another shop's local database, when signing in has just switched shop.
 */
export async function rememberForOffline(
  username: string,
  password: string,
  session: OfflineSession,
  target: PosDb = db,
): Promise<void> {
  if (!crypto?.subtle) return;
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await derive(password, salt, ITERATIONS);
  const row: StoredVerifier = {
    salt: toHex(salt),
    hash: toHex(hash),
    iterations: ITERATIONS,
    savedAt: new Date().toISOString(),
    session,
  };
  await target.meta.put({ key: keyFor(username), value: row });
}

export type OfflineResult =
  | { ok: true; session: OfflineSession }
  | { ok: false; reason: 'unknown' | 'wrong' | 'too_old' | 'unsupported' };

/** Check the password against what this device remembers. */
export async function tryOfflineSignIn(username: string, password: string): Promise<OfflineResult> {
  if (!crypto?.subtle) return { ok: false, reason: 'unsupported' };
  const row = await getMeta<StoredVerifier>(keyFor(username));
  if (!row) return { ok: false, reason: 'unknown' };
  const ageDays = (Date.now() - new Date(row.savedAt).getTime()) / 86_400_000;
  if (!(ageDays < MAX_AGE_DAYS)) return { ok: false, reason: 'too_old' };

  const hash = await derive(password, fromHex(row.salt), row.iterations);
  if (!equal(toHex(hash), row.hash)) return { ok: false, reason: 'wrong' };
  return { ok: true, session: row.session };
}

/** Forget one person, e.g. after their password changed or they were removed. */
export async function forgetOffline(username: string): Promise<void> {
  await db.meta.delete(keyFor(username));
}

/** Forget everyone who is no longer an active member of staff. */
export async function pruneOffline(activeUsernames: string[]): Promise<void> {
  const keep = new Set(activeUsernames.map((u) => keyFor(u)));
  const rows = await db.meta.where('key').startsWith(PREFIX).primaryKeys();
  await Promise.all(rows.filter((k) => !keep.has(String(k))).map((k) => db.meta.delete(k)));
}

// --- the arithmetic ----------------------------------------------------------

async function derive(password: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, base, 256);
  return new Uint8Array(bits);
}

const toHex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
const fromHex = (s: string) => Uint8Array.from((s.match(/.{2}/g) ?? []).map((h) => parseInt(h, 16)));

/** Compare without stopping at the first difference. */
function equal(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
